import { tr } from '../i18n';
import type { Lang, OwnerProfile } from '../store';
import { getProfile } from '../store';

function esc(s: string | undefined): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

export function renderClaimForm(lang: Lang): string {
  const L = tr(lang);
  const p = getProfile() as Partial<OwnerProfile>;

  return /* html */ `
    <div class="sq-modal" role="dialog" aria-modal="true" aria-labelledby="sq-form-title">
      <div class="sq-modal-card">
        <button class="sq-modal-close" id="sq-modal-close" aria-label="Close">×</button>

        <header class="sq-form-head">
          <h2 id="sq-form-title">${L.form_title}</h2>
          <p>${L.form_subtitle}</p>
        </header>

        <form id="sq-form" novalidate>
          <section class="sq-form-section">
            <div class="sq-form-section-title">${L.sec_dog}</div>

            <div class="sq-field">
              <label for="f-dog-name">${L.f_dog_name} *</label>
              <input id="f-dog-name" name="dog_name" type="text" maxlength="30" required value="${esc(p.dog_name)}" />
            </div>

            <div class="sq-field">
              <label for="f-dog-breed">${L.f_dog_breed} *</label>
              <input id="f-dog-breed" name="dog_breed" type="text" maxlength="40" required placeholder="${L.f_dog_breed_ph}" value="${esc(p.dog_breed)}" />
            </div>

            <div class="sq-grid-2">
              <div class="sq-field">
                <label for="f-dog-age">${L.f_dog_age} *</label>
                <select id="f-dog-age" name="dog_age_range" required>
                  <option value="">—</option>
                  ${L.age_ranges.map((a) => `<option value="${esc(a)}" ${p.dog_age_range === a ? 'selected' : ''}>${esc(a)}</option>`).join('')}
                </select>
              </div>
              <div class="sq-field">
                <label for="f-dog-gender">${L.f_dog_gender} *</label>
                <select id="f-dog-gender" name="dog_gender" required>
                  <option value="">—</option>
                  ${L.genders.map((g) => `<option value="${esc(g)}" ${p.dog_gender === g ? 'selected' : ''}>${esc(g)}</option>`).join('')}
                </select>
              </div>
            </div>

            <div class="sq-field">
              <label for="f-dog-size">${L.f_dog_size} *</label>
              <select id="f-dog-size" name="dog_size" required>
                <option value="">—</option>
                ${L.sizes.map((s) => `<option value="${esc(s)}" ${p.dog_size === s ? 'selected' : ''}>${esc(s)}</option>`).join('')}
              </select>
            </div>

            <div class="sq-field">
              <label for="f-dog-city">${L.f_dog_city} *</label>
              <input id="f-dog-city" name="dog_city" type="text" maxlength="40" required value="${esc(p.dog_city)}" />
            </div>

            <div class="sq-field">
              <label for="f-dog-superpower">${L.f_dog_superpower} *</label>
              <input id="f-dog-superpower" name="dog_superpower" type="text" maxlength="60" required placeholder="${L.f_dog_superpower_ph}" value="${esc(p.dog_superpower)}" />
            </div>

            <div class="sq-field">
              <label for="f-dog-hobby">${L.f_dog_hobby} *</label>
              <input id="f-dog-hobby" name="dog_hobby" type="text" maxlength="60" required placeholder="${L.f_dog_hobby_ph}" value="${esc(p.dog_hobby)}" />
            </div>

            <div class="sq-field">
              <label>${L.f_dog_photo} *</label>
              <div class="sq-photo-upload" id="sq-photo-upload">
                <input id="f-dog-photo" name="dog_photo" type="file" accept="image/jpeg,image/png,image/webp" required />
                <div class="sq-photo-placeholder" id="sq-photo-placeholder">
                  <div class="sq-photo-icon">📷</div>
                  <div class="sq-photo-hint">${L.f_dog_photo_hint}</div>
                </div>
                <div class="sq-photo-preview" id="sq-photo-preview" hidden>
                  <img id="sq-photo-img" alt="" />
                  <button type="button" class="sq-photo-change" id="sq-photo-change">${L.f_dog_photo_change}</button>
                </div>
              </div>
            </div>
          </section>

          <section class="sq-form-section">
            <div class="sq-form-section-title">${L.sec_owner}</div>

            <div class="sq-field">
              <label for="f-owner-name">${L.f_owner_name} *</label>
              <input id="f-owner-name" name="owner_name" type="text" maxlength="60" required value="${esc(p.owner_name)}" />
            </div>

            <div class="sq-field">
              <label for="f-handle">${L.f_handle} *</label>
              <input id="f-handle" name="instagram_handle" type="text" maxlength="40" required placeholder="${L.f_handle_ph}" value="${esc(p.instagram_handle)}" />
            </div>

            <div class="sq-grid-2">
              <div class="sq-field">
                <label for="f-email">${L.f_email} *</label>
                <input id="f-email" name="email" type="email" required value="${esc(p.email)}" />
              </div>
              <div class="sq-field">
                <label for="f-phone">${L.f_phone} *</label>
                <input id="f-phone" name="phone" type="tel" placeholder="+40..." required value="${esc(p.phone)}" />
              </div>
            </div>
          </section>

          <section class="sq-form-section">
            <div class="sq-form-section-title">${L.sec_consent}</div>

            <label class="sq-check">
              <input type="checkbox" name="consent_terms" required />
              <span>${L.consent_terms} *</span>
            </label>
            <label class="sq-check">
              <input type="checkbox" name="consent_content_usage" required />
              <span>${L.consent_content} *</span>
            </label>
            <label class="sq-check">
              <input type="checkbox" name="consent_newsletter" />
              <span>${L.consent_newsletter}</span>
            </label>
          </section>

          <div class="sq-form-error" id="sq-form-error" hidden></div>

          <div class="sq-form-actions">
            <button type="submit" class="sq-cta" id="sq-form-submit">${L.btn_submit}</button>
            <button type="button" class="sq-link" id="sq-reset-profile">${L.btn_reset_profile}</button>
          </div>
        </form>
      </div>
    </div>
  `;
}

export function renderConfirmation(
  lang: Lang,
  dogName: string,
  position: number,
  onWaitlist: boolean,
): string {
  const L = tr(lang);
  if (onWaitlist) {
    const waitlistPos = position - 5;
    return /* html */ `
      <div class="sq-modal" role="dialog" aria-modal="true">
        <div class="sq-modal-card sq-confirm sq-confirm-waitlist">
          <div class="sq-confirm-emoji">⏳</div>
          <div class="sq-confirm-name">${esc(dogName)}, ${L.conf_waitlist}</div>
          <div class="sq-confirm-slot sq-confirm-slot-wait">#${waitlistPos}</div>
          <div class="sq-confirm-din">${L.conf_waitlist_pos}</div>
          <p class="sq-confirm-note">${L.conf_waitlist_note}</p>
          <button class="sq-cta" id="sq-confirm-close">${L.conf_close}</button>
        </div>
      </div>
    `;
  }
  return /* html */ `
    <div class="sq-modal" role="dialog" aria-modal="true">
      <div class="sq-modal-card sq-confirm">
        <div class="sq-confirm-emoji">🎉</div>
        <div class="sq-confirm-name">${esc(dogName)} ${L.conf_title}</div>
        <div class="sq-confirm-slot">${L.conf_locul} ${position}</div>
        <div class="sq-confirm-din">${L.conf_locul_din}</div>
        <p class="sq-confirm-note">${L.conf_email}</p>
        <button class="sq-cta" id="sq-confirm-close">${L.conf_close}</button>
      </div>
    </div>
  `;
}
